import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export default function LeaderboardPage() {
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  const { data: leaderboardData, isLoading } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: api.getLeaderboard,
  });

  const { data: modelStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['modelStats', selectedModel],
    queryFn: () => api.getModelStats(selectedModel!),
    enabled: !!selectedModel,
  });

  const getRankBadge = (rank: number) => {
    if (rank === 1) return { bg: 'bg-yellow-500/20', text: 'text-yellow-500', icon: '🥇' };
    if (rank === 2) return { bg: 'bg-gray-400/20', text: 'text-gray-400', icon: '🥈' };
    if (rank === 3) return { bg: 'bg-orange-600/20', text: 'text-orange-600', icon: '🥉' };
    return { bg: 'bg-arena-border', text: 'text-arena-muted', icon: null };
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">ELO Leaderboard</h1>
        <p className="text-arena-muted text-lg">
          Rankings based on {leaderboardData?.total_votes.toLocaleString() || '...'} community votes
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Leaderboard */}
        <div className="lg:col-span-2">
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-arena-border">
              <h2 className="font-semibold">Scene Detection Models</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-arena-border text-arena-muted text-sm">
                  <th className="text-left p-4 font-medium">Rank</th>
                  <th className="text-left p-4 font-medium">Model</th>
                  <th className="text-right p-4 font-medium">ELO</th>
                  <th className="text-right p-4 font-medium">W-L-T</th>
                  <th className="text-right p-4 font-medium">Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-arena-muted">
                      Loading rankings...
                    </td>
                  </tr>
                ) : leaderboardData?.leaderboard.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-arena-muted">
                      No battles yet. Be the first to vote!
                    </td>
                  </tr>
                ) : (
                  leaderboardData?.leaderboard.map((model, index) => {
                    const badge = getRankBadge(index + 1);
                    const isSelected = selectedModel === model.id;
                    return (
                      <tr
                        key={model.id}
                        onClick={() => setSelectedModel(isSelected ? null : model.id)}
                        className={`border-b border-arena-border last:border-0 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-arena-accent/10'
                            : 'hover:bg-arena-surface/50'
                        }`}
                      >
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold ${badge.bg} ${badge.text}`}
                          >
                            {badge.icon || index + 1}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="font-medium">{model.display_name}</div>
                          {model.description && (
                            <div className="text-sm text-arena-muted truncate max-w-xs">
                              {model.description}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-mono text-xl font-bold">
                            {model.elo_rating}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-arena-success">{model.wins}</span>
                          <span className="text-arena-muted mx-1">-</span>
                          <span className="text-arena-error">{model.losses}</span>
                          <span className="text-arena-muted mx-1">-</span>
                          <span className="text-arena-muted">{model.ties}</span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 bg-arena-border rounded-full overflow-hidden">
                              <div
                                className="h-full bg-arena-success"
                                style={{ width: `${model.win_rate}%` }}
                              />
                            </div>
                            <span className="font-mono text-sm w-12 text-right">
                              {model.win_rate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* How ELO Works */}
          <div className="card p-6 mt-6">
            <h3 className="font-semibold mb-4">How ELO Rating Works</h3>
            <div className="text-sm text-arena-muted space-y-2">
              <p>
                ELO is a rating system originally designed for chess. Each model starts at 1500.
              </p>
              <p>
                When you vote, the winner gains points and the loser loses points. The amount
                depends on the expected outcome - beating a higher-rated model earns more points.
              </p>
              <p>
                Ties result in smaller rating adjustments. Skipped battles don't affect ratings.
              </p>
            </div>
          </div>
        </div>

        {/* Model Details Sidebar */}
        <div className="lg:col-span-1">
          {selectedModel ? (
            <div className="card p-6 sticky top-4">
              {isLoadingStats ? (
                <div className="text-center text-arena-muted py-8">
                  Loading model details...
                </div>
              ) : modelStats ? (
                <>
                  <h3 className="text-xl font-bold mb-2">
                    {modelStats.model.display_name}
                  </h3>
                  {modelStats.model.description && (
                    <p className="text-arena-muted text-sm mb-4">
                      {modelStats.model.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-arena-surface rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold font-mono">
                        {modelStats.model.elo_rating}
                      </div>
                      <div className="text-sm text-arena-muted">ELO Rating</div>
                    </div>
                    <div className="bg-arena-surface rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold font-mono">
                        {modelStats.model.win_rate}%
                      </div>
                      <div className="text-sm text-arena-muted">Win Rate</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="text-sm text-arena-muted mb-2">Battle Record</div>
                    <div className="flex gap-4">
                      <div>
                        <span className="text-arena-success font-bold">{modelStats.model.wins}</span>
                        <span className="text-arena-muted ml-1">wins</span>
                      </div>
                      <div>
                        <span className="text-arena-error font-bold">{modelStats.model.losses}</span>
                        <span className="text-arena-muted ml-1">losses</span>
                      </div>
                      <div>
                        <span className="text-arena-muted font-bold">{modelStats.model.ties}</span>
                        <span className="text-arena-muted ml-1">ties</span>
                      </div>
                    </div>
                  </div>

                  {modelStats.head_to_head.length > 0 && (
                    <div className="mb-6">
                      <div className="text-sm text-arena-muted mb-2">Head-to-Head</div>
                      <div className="space-y-2">
                        {modelStats.head_to_head.map((h2h: any) => (
                          <div
                            key={h2h.opponent_id}
                            className="flex items-center justify-between text-sm"
                          >
                            <span>{h2h.opponent_name}</span>
                            <span className="font-mono">
                              <span className="text-arena-success">{h2h.wins}</span>
                              <span className="text-arena-muted">-</span>
                              <span className="text-arena-error">{h2h.losses}</span>
                              <span className="text-arena-muted">-</span>
                              <span>{h2h.ties}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {modelStats.model.source_url && (
                    <a
                      href={modelStats.model.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary w-full text-sm"
                    >
                      View Source →
                    </a>
                  )}
                </>
              ) : null}
            </div>
          ) : (
            <div className="card p-6 text-center text-arena-muted">
              <div className="text-4xl mb-4">👆</div>
              <p>Click on a model to see detailed statistics</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
